import React from "react";
import { Drawer, Form, Select, Switch, Icon, Button, message } from "antd";
import { history } from '../appRedux/store';

const { Option } = Select;

const CONFIG_DESCRIPTIONS = {
    default_theme: "Applies to the overall application",
    default_ris_id: "Preselected Organisation when you land on any worklist",
    default_mip_id: "Preselected site used to filter your worklists",
    deadman_switch: "Requires a physical control to be held to continue dictation.",
    show_my_cases_only: "Filter worklists to only cases assigned to you by default.",
    default_editor_theme: "Colour theme applied inside the report editor",
    exit_visit_no_confirm: "Skip the \"are you sure?\" prompt when exiting a visit.",
    auto_launch_iv_sfv: "Open InteleViewer automatically when a case is opened from To Be Verified, including auto-next. View Images still works when this is off.",
};

const ConfigRow = ({ label, description, control, layout = "stack" }) =>
    layout === "inline" ? (
        <div className="user-config-row inline">
            <div className="user-config-row-text">
                <div className="user-config-row-label">{label}</div>
                {description && (
                    <div className="user-config-row-desc">{description}</div>
                )}
            </div>
            <div className="user-config-row-control">{control}</div>
        </div>
    ) : (
        <div className="user-config-row stack">
            <div className="user-config-row-label">{label}</div>
            {description && (
                <div className="user-config-row-desc">{description}</div>
            )}
            <div className="user-config-row-control">{control}</div>
        </div>
    );

class UserConfigurationsModalForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            submitting: false,
            risUserMips: [],
        };
    }

    componentWillMount() {
        const { authUser } = this.props;
        this.setState({
            risUserMips: authUser && authUser.risUserMips ? authUser.risUserMips : [],
        });
    }

    componentDidMount() {
        if (this.props.fetchConfigurations) {
            this.props.fetchConfigurations();
        }
    }

    componentDidUpdate(prevProps) {
        const { configurations } = this.props;
        if (configurations && prevProps.configurations !== configurations) {
            this.props.form.setFieldsValue({
                default_theme: configurations.default_theme,
                default_ris_id: configurations.default_ris_id,
                default_mip_id: configurations.default_mip_id ? configurations.default_mip_id : undefined,
                default_editor_theme: configurations.default_editor_theme,
                deadman_switch: configurations.deadman_switch,
                show_my_cases_only: configurations.show_my_cases_only,
                exit_visit_no_confirm: configurations.exit_visit_no_confirm,
                auto_launch_iv_sfv: configurations.auto_launch_iv_sfv !== false,
            });
        }
    }

    handleSubmit = (e) => {
        e.preventDefault();
        this.props.form.validateFields((err, values) => {
            if (!err) {
                let configurations = [];
                Object.keys(values).forEach((key) => {
                    configurations.push({ name: key, value: values[key] });
                });
                this.setState({ submitting: true });
                this.props.onOk(configurations, () => {
                    history.push(`/${this.props.authUser.role.replace("ROLE_", "").toLowerCase()}/dashboard`);
                    this.props.onCancel();
                });
            } else {
                message.error("Please select all default configurations");
            }
        });
    };

    getMipsByRisId = (risId) => {
        if (risId === "" || risId === 0 || risId === null || risId === undefined) {
            risId = this.props.configurations.default_ris_id;
        }
        const { risUserMips } = this.state;
        const filteredData = risUserMips.find((risUserMip) => risUserMip.risId === risId);
        return filteredData && filteredData.userMips ? filteredData.userMips : [];
    };

    render() {
        const { form, visible, onCancel, configurations } = this.props;
        const { getFieldDecorator, getFieldValue } = form;
        const { risUserMips } = this.state;
        const selectedRisId = getFieldValue("default_ris_id");
        const mips = this.getMipsByRisId(selectedRisId);

        const selectStyle = { width: "100%", textTransform: "uppercase" };

        return (
            <Drawer
                title="Default Configurations"
                placement="right"
                width={640}
                visible={visible}
                onClose={onCancel}
                closable={true}
                maskClosable={true}
                destroyOnClose
                className="user-config-drawer"
            >
                <Form className="user-config-form" hideRequiredMark>
                    {/* --- Application Defaults ------------------------------- */}
                    <div className="user-config-section-title">Application Defaults</div>

                    <Form.Item className="user-config-item">
                        <ConfigRowLabels
                            label="Default Theme"
                            description={CONFIG_DESCRIPTIONS.default_theme}
                        />
                        {getFieldDecorator("default_theme", {
                            initialValue: configurations.default_theme,
                        })(
                            <Select placeholder="Select default theme" allowClear style={selectStyle}>
                                <Option value="THEME_TYPE_LITE">LIGHT</Option>
                                <Option value="THEME_TYPE_DARK">DARK</Option>
                            </Select>
                        )}
                    </Form.Item>

                    <Form.Item className="user-config-item">
                        <ConfigRowLabels
                            label="Default RIS"
                            description={CONFIG_DESCRIPTIONS.default_ris_id}
                        />
                        {getFieldDecorator("default_ris_id", {
                            initialValue: configurations.default_ris_id,
                        })(
                            <Select
                                placeholder="Select default RIS"
                                allowClear
                                style={selectStyle}
                                onChange={() => this.props.form.setFieldsValue({ mips: [] })}
                            >
                                {risUserMips.map((risUserMip, index) => (
                                    <Option key={index} value={risUserMip.risId}>{risUserMip.risCode}</Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>

                    <Form.Item className="user-config-item">
                        <ConfigRowLabels
                            label="Default Site"
                            description={CONFIG_DESCRIPTIONS.default_mip_id}
                        />
                        {getFieldDecorator("default_mip_id", {
                            initialValue: configurations.default_mip_id ? configurations.default_mip_id : undefined,
                        })(
                            <Select
                                placeholder="Select default site"
                                allowClear
                                style={selectStyle}
                            >
                                {mips.map((mip, index) => (
                                    <Option key={index} value={mip.mipId}>{mip.mipName}</Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>

                    {/* --- Editor -------------------------------------------- */}
                    <div className="user-config-section-title">Editor</div>

                    <Form.Item className="user-config-item">
                        <ConfigRowLabels
                            label="Editor Theme"
                            description={CONFIG_DESCRIPTIONS.default_editor_theme}
                        />
                        {getFieldDecorator("default_editor_theme", {
                            initialValue: configurations.default_editor_theme,
                        })(
                            <Select placeholder="Select editor theme" allowClear style={selectStyle}>
                                <Option value="LIGHT">LIGHT</Option>
                                <Option value="DARK">DARK</Option>
                            </Select>
                        )}
                    </Form.Item>

                    {/* --- Workflow ------------------------------------------ */}
                    <div className="user-config-section-title">Workflow</div>

                    <Form.Item className="user-config-item inline">
                        <div className="user-config-inline-body">
                            <div className="user-config-row-text">
                                <div className="user-config-row-label">Deadman Switch</div>
                                <div className="user-config-row-desc">{CONFIG_DESCRIPTIONS.deadman_switch}</div>
                            </div>
                            <div className="user-config-row-control">
                                {getFieldDecorator("deadman_switch", {
                                    initialValue: configurations.deadman_switch,
                                    valuePropName: "checked",
                                })(
                                    <Switch
                                        checkedChildren={<Icon type="check" />}
                                        unCheckedChildren={<Icon type="close" />}
                                    />
                                )}
                            </div>
                        </div>
                    </Form.Item>

                    <Form.Item className="user-config-item inline">
                        <div className="user-config-inline-body">
                            <div className="user-config-row-text">
                                <div className="user-config-row-label">Show My Cases Only</div>
                                <div className="user-config-row-desc">{CONFIG_DESCRIPTIONS.show_my_cases_only}</div>
                            </div>
                            <div className="user-config-row-control">
                                {getFieldDecorator("show_my_cases_only", {
                                    initialValue: configurations.show_my_cases_only,
                                    valuePropName: "checked",
                                })(
                                    <Switch
                                        checkedChildren={<Icon type="check" />}
                                        unCheckedChildren={<Icon type="close" />}
                                    />
                                )}
                            </div>
                        </div>
                    </Form.Item>

                    <Form.Item className="user-config-item inline">
                        <div className="user-config-inline-body">
                            <div className="user-config-row-text">
                                <div className="user-config-row-label">Skip Exit Confirmation</div>
                                <div className="user-config-row-desc">{CONFIG_DESCRIPTIONS.exit_visit_no_confirm}</div>
                            </div>
                            <div className="user-config-row-control">
                                {getFieldDecorator("exit_visit_no_confirm", {
                                    initialValue: configurations.exit_visit_no_confirm,
                                    valuePropName: "checked",
                                })(
                                    <Switch
                                        checkedChildren={<Icon type="check" />}
                                        unCheckedChildren={<Icon type="close" />}
                                    />
                                )}
                            </div>
                        </div>
                    </Form.Item>

                    <Form.Item className="user-config-item inline">
                        <div className="user-config-inline-body">
                            <div className="user-config-row-text">
                                <div className="user-config-row-label">Auto-Launch InteleViewer on To Be Verified</div>
                                <div className="user-config-row-desc">{CONFIG_DESCRIPTIONS.auto_launch_iv_sfv}</div>
                            </div>
                            <div className="user-config-row-control">
                                {getFieldDecorator("auto_launch_iv_sfv", {
                                    initialValue: configurations.auto_launch_iv_sfv !== false,
                                    valuePropName: "checked",
                                })(
                                    <Switch
                                        checkedChildren={<Icon type="check" />}
                                        unCheckedChildren={<Icon type="close" />}
                                    />
                                )}
                            </div>
                        </div>
                    </Form.Item>
                </Form>
                <div className="user-config-drawer-footer">
                    <Button
                        className="user-config-cancel-btn"
                        onClick={onCancel}
                        disabled={this.state.submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        className="user-config-save-btn"
                        onClick={this.handleSubmit}
                        loading={this.state.submitting}
                    >
                        Save changes
                    </Button>
                </div>
            </Drawer>
        );
    }
}

const ConfigRowLabels = ({ label, description }) => (
    <div className="user-config-row-text stacked">
        <div className="user-config-row-label">{label}</div>
        {description && <div className="user-config-row-desc">{description}</div>}
    </div>
);

const UserConfigurationsModal = Form.create()(UserConfigurationsModalForm);

export default UserConfigurationsModal;
